import { RapidStateCreator } from "../types";
import {
    subscribeToJobs,
    uploadFilesToBatch,
    triggerBatchJob,
    getUploadErrorReason,
    type BatchJob
} from "@rapid/utils/api/batchProcessing";

export type BatchJobUploadState = "idle" | "uploading" | "triggering" | "done" | "error";

export interface FailedFileInfo {
    fileName: string;
    reason: string;
}

export interface BatchProcessingSliceState {
    // State
    batchJobs: BatchJob[];
    batchJobsLoading: boolean;
    batchJobUploadState: BatchJobUploadState;
    batchJobUploadProgress: { completed: number; total: number } | null;
    batchJobUploadError: string | null;
    batchJobFailedFiles: FailedFileInfo[];

    // Internal (not persisted)
    _batchJobsUnsubscribe: (() => void) | null;

    // Actions
    setBatchJobs: (jobs: BatchJob[]) => void;
    startBatchJobsSubscription: (workspaceId: string) => void;
    stopBatchJobsSubscription: () => void;
    uploadAndTriggerBatchJob: (params: {
        workspaceId: string;
        files: File[];
        jobType: "images" | "videos";
        workflowCredentials: { apiKey: string; workspaceUrl: string; workflowUrl: string };
    }) => Promise<void>;
    resetBatchJobUploadState: () => void;
    clearBatchProcessingState: () => void;
}

export const createBatchProcessingSlice: RapidStateCreator<BatchProcessingSliceState> = (
    set,
    get
) => ({
    batchJobs: [],
    batchJobsLoading: false,
    batchJobUploadState: "idle",
    batchJobUploadProgress: null,
    batchJobUploadError: null,
    batchJobFailedFiles: [],
    _batchJobsUnsubscribe: null,

    setBatchJobs: (jobs: BatchJob[]) => set({ batchJobs: jobs }),

    startBatchJobsSubscription: (workspaceId: string) => {
        // Stop any existing subscription first
        const existing = get()._batchJobsUnsubscribe;
        if (existing) {
            existing();
        }

        set({ batchJobsLoading: true });

        const database = (get() as any)._database;
        const unsubscribe = subscribeToJobs(
            workspaceId,
            (jobs: BatchJob[]) => {
                set({ batchJobs: jobs, batchJobsLoading: false });
            },
            database
        );

        set({ _batchJobsUnsubscribe: unsubscribe });
    },

    stopBatchJobsSubscription: () => {
        const unsubscribe = get()._batchJobsUnsubscribe;
        if (unsubscribe) {
            unsubscribe();
        }
        set({ _batchJobsUnsubscribe: null, batchJobs: [], batchJobsLoading: false });
    },

    uploadAndTriggerBatchJob: async (params) => {
        const { workspaceId, files, jobType, workflowCredentials } = params;
        const batchId = crypto.randomUUID();
        const jobId = "rapid-" + crypto.randomUUID().replace(/-/g, "").slice(0, 14);

        try {
            set({
                batchJobUploadState: "uploading",
                batchJobUploadProgress: { completed: 0, total: 0 },
                batchJobUploadError: null,
                batchJobFailedFiles: []
            });

            const { successCount, failedFiles } = await uploadFilesToBatch(
                workspaceId,
                files,
                batchId,
                jobType,
                (completed, total) => {
                    set({ batchJobUploadProgress: { completed, total } });
                }
            );

            const failedFileInfos = failedFiles.map(({ file, error }) => ({
                fileName: file.name,
                reason: getUploadErrorReason(error)
            }));

            if (successCount === 0) {
                set({
                    batchJobUploadState: "error",
                    batchJobUploadError: "All files failed to upload",
                    batchJobFailedFiles: failedFileInfos,
                    batchJobUploadProgress: null
                });
                return;
            }

            if (failedFileInfos.length > 0) {
                set({ batchJobFailedFiles: failedFileInfos });
            }

            set({ batchJobUploadState: "triggering" });

            await triggerBatchJob(workspaceId, {
                apiKey: workflowCredentials.apiKey,
                jobId,
                jobName: `Rapid ${jobType} batch - ${successCount} files`,
                jobType,
                batchId,
                workspaceUrl: workflowCredentials.workspaceUrl,
                workflowUrl: workflowCredentials.workflowUrl
            });

            set({ batchJobUploadState: "done", batchJobUploadProgress: null });
        } catch (error) {
            console.error("Batch processing error:", error);
            set({
                batchJobUploadState: "error",
                batchJobUploadError:
                    error instanceof Error ? error.message : "Failed to process batch",
                batchJobUploadProgress: null
            });
        }
    },

    resetBatchJobUploadState: () => {
        set({
            batchJobUploadState: "idle",
            batchJobUploadProgress: null,
            batchJobUploadError: null,
            batchJobFailedFiles: []
        });
    },

    clearBatchProcessingState: () => {
        const unsubscribe = get()._batchJobsUnsubscribe;
        if (unsubscribe) {
            unsubscribe();
        }
        set({
            batchJobs: [],
            batchJobsLoading: false,
            batchJobUploadState: "idle",
            batchJobUploadProgress: null,
            batchJobUploadError: null,
            batchJobFailedFiles: [],
            _batchJobsUnsubscribe: null
        });
    }
});
